import { actionResultIsError } from 'src/lib/reduxUtils'
import { ERROR_DEFAULT } from 'src/messages/assets'
import QueryClientUpdater from 'src/new_arch/services/QueryClientUpdater'
import type Asset from 'src/new_arch/modules/projectView/models/asset'
import type { AssetRoundJSON } from 'src/new_arch/modules/projectView/models/assetRound'
import type SnackbarSystem from '@ui/systems/SnackbarSystem'

import { UNDO_DELETE_COPY, UNDO_DELETE_DELAY_MS } from '../constants'
import type PendingDeletesStore from '../stores/PendingDeletesStore'
import type {
	PendingDelete, PendingItemsDelete, PendingRoundDelete, PendingLastRoundDelete
} from '../stores/PendingDeletesStore'

type LastRoundDeletable = { id: number }

type ReduxAssetSystem = {
	deleteAssets: ( params: { projectID: number, assetIDs: number[] } ) => Promise<unknown>,
	deleteAssetsLastRound: ( projectID: number, assets: LastRoundDeletable[] ) => Promise<unknown>
}

type DeletableAsset = { id: number, name: string }

type UndoableDeleteSystemParams = {
	store: PendingDeletesStore,
	snackbarSystem: SnackbarSystem,
	assetSystem: ReduxAssetSystem,
	selectAssetRound: ( roundID: number ) => void,
	deleteCurrentRound: ( asset: Asset ) => Promise<unknown>,
	fetchAssetRounds: ( assetID: number ) => Promise<AssetRoundJSON[]>
}

let pendingDeleteSequence = 0
const generatePendingDeleteID = () => {
	pendingDeleteSequence += 1
	return `undo-delete-${pendingDeleteSequence}`
}

export default class UndoableDeleteSystem {
	private store: PendingDeletesStore
	private snackbarSystem: SnackbarSystem
	private assetSystem: ReduxAssetSystem
	private selectAssetRound: ( roundID: number ) => void
	private deleteCurrentRound: ( asset: Asset ) => Promise<unknown>
	private fetchAssetRounds: ( assetID: number ) => Promise<AssetRoundJSON[]>

	constructor( {
		store, snackbarSystem, assetSystem, selectAssetRound, deleteCurrentRound, fetchAssetRounds
	}: UndoableDeleteSystemParams ) {
		this.store = store
		this.snackbarSystem = snackbarSystem
		this.assetSystem = assetSystem
		this.selectAssetRound = selectAssetRound
		this.deleteCurrentRound = deleteCurrentRound
		this.fetchAssetRounds = fetchAssetRounds
	}

	scheduleItemsDelete( {
		projectID, assets, onScheduled
	}: {
		projectID: number,
		assets: DeletableAsset[],
		onScheduled?: () => void
	} ) {
		const assetIDs = assets.map( asset => asset.id )
		if ( assetIDs.length === 0 ) return

		const id = generatePendingDeleteID()
		const isBulk = assetIDs.length > 1
		const primaryName = isBulk ? undefined : assets[ 0 ]?.name

		QueryClientUpdater.itemsWereOptimisticallyDeleted( { projectID, assetIDs } )

		const timeoutID = setTimeout( () => this.commit( id ), UNDO_DELETE_DELAY_MS )

		this.store.add( {
			id, kind: 'items', projectID, assetIDs, isBulk, primaryName, timeoutID, isUndoable: true
		} )

		this.snackbarSystem.showOutlinedMessageWithAction( {
			id,
			title: isBulk ? UNDO_DELETE_COPY.assetsDeleted : UNDO_DELETE_COPY.assetDeleted,
			description: primaryName,
			type: 'warning',
			icon: 'delete',
			action: { label: UNDO_DELETE_COPY.undo, onClick: () => this.undo( id ) },
			autoClose: UNDO_DELETE_DELAY_MS,
			onClose: () => this.store.markAsNotUndoable( id )
		} )

		onScheduled?.()
	}

	scheduleRoundDelete( {
		projectID, asset, previousRoundID
	}: { projectID: number, asset: Asset, previousRoundID: number } ) {
		const assetID = asset.id
		const { currentRoundID } = asset

		this.selectAssetRound( previousRoundID )

		const id = generatePendingDeleteID()
		const timeoutID = setTimeout( () => this.commit( id ), UNDO_DELETE_DELAY_MS )

		this.store.add( {
			id, kind: 'round', projectID, asset, assetID, currentRoundID, previousRoundID, timeoutID, isUndoable: true
		} )

		this.snackbarSystem.showOutlinedMessageWithAction( {
			id,
			title: UNDO_DELETE_COPY.roundDeleted,
			type: 'warning',
			icon: 'delete',
			action: { label: UNDO_DELETE_COPY.undo, onClick: () => this.undo( id ) },
			autoClose: UNDO_DELETE_DELAY_MS,
			onClose: () => this.store.markAsNotUndoable( id )
		} )
	}

	async scheduleLastRoundDelete( { projectID, assets }: { projectID: number, assets: LastRoundDeletable[] } ) {
		const fetched = await Promise.allSettled(
			assets.map( async ( asset ) => {
				const rounds = await this.fetchAssetRounds( asset.id )
				const sortedDesc = [ ...rounds ].sort( ( a, b ) => b.number - a.number )
				return { asset, deletedRound: sortedDesc[ 0 ], previousRound: sortedDesc[ 1 ] }
			} )
		)

		if ( fetched.some( result => result.status === 'rejected' ) ) {
			this.snackbarSystem.showErrorMessage( { title: ERROR_DEFAULT } )
			return
		}

		const revertable = fetched
			.flatMap( result => ( result.status === 'fulfilled' ? [ result.value ] : [] ) )
			.filter( ( { deletedRound, previousRound } ) => deletedRound && previousRound )
		if ( revertable.length === 0 ) return

		revertable.forEach( ( { asset, previousRound } ) => {
			QueryClientUpdater.lastRoundWasOptimisticallyDeleted( { projectID, assetID: asset.id, previousRound } )
		} )

		const id = generatePendingDeleteID()
		const timeoutID = setTimeout( () => this.commit( id ), UNDO_DELETE_DELAY_MS )

		this.store.add( {
			id,
			kind: 'lastRound',
			projectID,
			assets: revertable.map( ( { asset } ) => asset ),
			reverts: revertable.map( ( { asset, deletedRound, previousRound } ) => (
				{ assetID: asset.id, deletedRound, previousRound }
			) ),
			timeoutID,
			isUndoable: true
		} )

		this.snackbarSystem.showOutlinedMessageWithAction( {
			id,
			title: UNDO_DELETE_COPY.roundDeleted,
			type: 'warning',
			icon: 'delete',
			action: { label: UNDO_DELETE_COPY.undo, onClick: () => this.undo( id ) },
			autoClose: UNDO_DELETE_DELAY_MS,
			onClose: () => this.store.markAsNotUndoable( id )
		} )
	}

	undo( id: string ) {
		const pendingDelete = this.store.find( id )
		if ( !pendingDelete ) return

		clearTimeout( pendingDelete.timeoutID )
		this.store.remove( id )
		this.snackbarSystem.hideMessage( id )

		try {
			this.restore( pendingDelete )
			this.showRestoredMessage( pendingDelete )
		} catch {
			this.snackbarSystem.showErrorMessage( { title: ERROR_DEFAULT } )
		}
	}

	undoMostRecent() {
		const pendingDelete = this.store.mostRecentUndoable
		if ( pendingDelete ) this.undo( pendingDelete.id )
	}

	dismissUndoablesOutsideProject( currentProjectID: number | undefined ) {
		this.store.pendingDeletes
			.filter( pendingDelete => pendingDelete.isUndoable && pendingDelete.projectID !== currentProjectID )
			.forEach( ( pendingDelete ) => {
				this.snackbarSystem.hideMessage( pendingDelete.id )
				this.store.markAsNotUndoable( pendingDelete.id )
			} )
	}

	async commit( id: string ) {
		const pendingDelete = this.store.find( id )
		if ( !pendingDelete ) return

		this.store.remove( id )
		this.snackbarSystem.hideMessage( id )

		if ( pendingDelete.kind === 'round' ) {
			await this.commitRound( pendingDelete )
			return
		}

		if ( pendingDelete.kind === 'lastRound' ) {
			await this.commitLastRound( pendingDelete )
			return
		}

		await this.commitItems( pendingDelete )
	}

	private restore( pendingDelete: PendingDelete ) {
		if ( pendingDelete.kind === 'round' ) {
			this.selectAssetRound( pendingDelete.currentRoundID )
			return
		}

		if ( pendingDelete.kind === 'lastRound' ) {
			this.restoreLastRounds( pendingDelete )
			return
		}

		this.restoreItemsCache( pendingDelete.projectID )
	}

	private restoreLastRounds( pendingDelete: PendingLastRoundDelete ) {
		pendingDelete.reverts.forEach( ( revert ) => {
			QueryClientUpdater.lastRoundDeletionWasUndone( {
				projectID: pendingDelete.projectID, assetID: revert.assetID, deletedRound: revert.deletedRound
			} )
		} )

		if ( pendingDelete.reverts.length === 1 ) {
			this.selectAssetRound( pendingDelete.reverts[ 0 ].deletedRound.id )
		}
	}

	private restoreItemsCache( projectID: number ) {
		return this.refetchItemsAndReapplyPending( projectID )
			.catch( () => this.snackbarSystem.showErrorMessage( { title: ERROR_DEFAULT } ) )
	}

	private refetchItemsAndReapplyPending( projectID: number ) {
		return QueryClientUpdater.itemsDeletionWasUndone( { projectID } )
			.then( () => this.reapplyPendingOptimisticState( projectID ) )
	}

	private reapplyPendingOptimisticState( projectID: number ) {
		this.store.pendingDeletes
			.filter( pending => pending.projectID === projectID )
			.forEach( ( pending ) => {
				if ( pending.kind === 'items' ) {
					QueryClientUpdater.itemsWereOptimisticallyDeleted( {
						projectID, assetIDs: pending.assetIDs
					} )
				} else if ( pending.kind === 'lastRound' ) {
					pending.reverts.forEach( ( revert ) => {
						QueryClientUpdater.lastRoundWasOptimisticallyDeleted( {
							projectID, assetID: revert.assetID, previousRound: revert.previousRound
						} )
					} )
				}
			} )
	}

	private showRestoredMessage( pendingDelete: PendingDelete ) {
		if ( pendingDelete.kind === 'round' || pendingDelete.kind === 'lastRound' ) {
			this.snackbarSystem.showOutlinedMessageWithAction( {
				title: UNDO_DELETE_COPY.roundRestored, type: 'success'
			} )
			return
		}

		this.snackbarSystem.showOutlinedMessageWithAction( {
			title: pendingDelete.isBulk ? UNDO_DELETE_COPY.assetsRestored : UNDO_DELETE_COPY.assetRestored,
			description: pendingDelete.primaryName,
			type: 'success'
		} )
	}

	private async commitItems( pendingDelete: PendingItemsDelete ) {
		const { projectID, assetIDs } = pendingDelete

		try {
			const result = await this.assetSystem.deleteAssets( { projectID, assetIDs } )

			if ( result && actionResultIsError( result ) ) {
				throw new Error( 'Undoable delete commit failed' )
			}
		} catch {
			this.refetchItemsAndReapplyPending( projectID ).catch( () => {} )
			this.snackbarSystem.showErrorMessage( { title: ERROR_DEFAULT } )
		}
	}

	private async commitRound( pendingDelete: PendingRoundDelete ) {
		const { asset, currentRoundID } = pendingDelete

		// Restore the current-round selection before deleting so the legacy action derives
		// the right previous round (it computes it from the selected round).
		this.selectAssetRound( currentRoundID )

		try {
			const result = await this.deleteCurrentRound( asset )

			if ( result && actionResultIsError( result ) ) {
				throw new Error( 'Undoable round delete commit failed' )
			}
		} catch {
			this.snackbarSystem.showErrorMessage( { title: ERROR_DEFAULT } )
		}
	}

	private async commitLastRound( pendingDelete: PendingLastRoundDelete ) {
		const { projectID, assets, reverts } = pendingDelete

		try {
			const result = await this.assetSystem.deleteAssetsLastRound( projectID, assets )

			if ( result && actionResultIsError( result ) ) {
				throw new Error( 'Undoable last round delete commit failed' )
			}

			// The legacy delete refetches the lists, which would clobber other still-pending
			// optimistic states, so we refetch ourselves and reapply them once it settles.
			await QueryClientUpdater.itemsDeletionWasUndone( { projectID } )
			this.reapplyPendingOptimisticState( projectID )

			reverts.forEach( ( revert ) => {
				QueryClientUpdater.lastRoundDeletionWasCommitted( { assetID: revert.assetID } )
			} )
		} catch {
			reverts.forEach( ( revert ) => {
				QueryClientUpdater.lastRoundDeletionWasUndone( {
					projectID, assetID: revert.assetID, deletedRound: revert.deletedRound
				} )
			} )
			this.snackbarSystem.showErrorMessage( { title: ERROR_DEFAULT } )
		}
	}
}
